/**
 * flowTemplates.js — Plantillas de flujo listas para usar con 1 clic.
 *
 * Cada plantilla tiene la MISMA forma que el objeto que `Flows.svelte:newFlow()`
 * envía a `flowsAPI.create()`:
 *   { name, is_active, trigger_type, trigger_config, start_node_id, nodes }
 * donde nodes = { [id]: { id, type, config, next, branches? } }.
 *
 * Los IDs de nodo aquí son "lógicos" (s, ask, etc.); `instantiate()` los
 * reescribe a IDs únicos (n_<base36>) al crear cada flujo, para que dos flujos
 * creados desde la misma plantilla nunca compartan IDs.
 *
 * El esquema por tipo de nodo está validado contra flow_engine.py:
 *   message   {text}
 *   ask       {question, var_name}                 (pausa hasta la respuesta)
 *   menu      {question, var_name, options:[{label}], max_attempts}  branches:{'0'..N,'other'}
 *   condition {var_name, op, value}                branches:{yes,no}
 *   label     {labels:[], op:'add'|'remove'}
 *   assign    {team_id?|user_id?}
 *   handoff   {reason?}                             (terminal; escala a humano)
 *   end       {}
 */

export const FLOW_TEMPLATES = [
	// ─────────────────────────────────────────────────────────────────────
	{
		id: 'welcome',
		name: 'Bienvenida automática',
		description: 'Saluda al contacto nuevo, le pide su nombre y responde personalizado.',
		icon: 'sparkles',
		trigger_type: 'new_conversation',
		trigger_config: {},
		start_node_id: 's',
		nodes: {
			s:    { id: 's',    type: 'message', config: { text: '¡Hola! 👋 Bienvenid@ a nuestro WhatsApp. Gracias por escribirnos.' }, next: 'ask' },
			ask:  { id: 'ask',  type: 'ask',     config: { question: 'Para atenderte mejor, ¿cuál es tu nombre? 😊', var_name: 'nombre' }, next: 'reply' },
			reply:{ id: 'reply',type: 'message', config: { text: '¡Un gusto, {{nombre}}! 🙌 En un momento te atendemos. Si querés, contanos en qué te podemos ayudar.' }, next: 'fin' },
			fin:  { id: 'fin',  type: 'end',     config: {}, next: null }
		}
	},

	// ─────────────────────────────────────────────────────────────────────
	{
		id: 'menu',
		name: 'Menú de opciones',
		description: 'Envía un menú numerado (Ventas / Soporte / Otro) y deriva según la opción elegida.',
		icon: 'list',
		trigger_type: 'message_in',
		trigger_config: {},
		start_node_id: 'menu',
		nodes: {
			menu: {
				id: 'menu', type: 'menu',
				config: {
					question: '¿En qué te podemos ayudar? Responde con el número:',
					var_name: 'opcion',
					options: [{ label: 'Ventas' }, { label: 'Soporte' }, { label: 'Otra consulta' }],
					max_attempts: 2,
					retry_text: 'No te entendí 🙏 — responde con el número de la opción.'
				},
				branches: { '0': 'ventas', '1': 'soporte', '2': 'otro', 'other': 'otro' },
				next: null
			},
			ventas:  { id: 'ventas',  type: 'message', config: { text: '¡Genial! 🛍️ Te paso con nuestro equipo de Ventas.' }, next: 'hVentas' },
			hVentas: { id: 'hVentas', type: 'handoff', config: { reason: 'Solicitó Ventas desde el menú' }, next: null },
			soporte: { id: 'soporte', type: 'message', config: { text: '🛠️ Entendido, te conecto con Soporte para ayudarte.' }, next: 'hSoporte' },
			hSoporte:{ id: 'hSoporte',type: 'handoff', config: { reason: 'Solicitó Soporte desde el menú' }, next: null },
			otro:    { id: 'otro',    type: 'message', config: { text: 'Perfecto, contanos tu consulta y un agente te responderá en breve. 🙌' }, next: 'fin' },
			fin:     { id: 'fin',     type: 'end',     config: {}, next: null }
		}
	},

	// ─────────────────────────────────────────────────────────────────────
	{
		id: 'lead_qualify',
		name: 'Calificación de leads',
		description: 'Pregunta el presupuesto, etiqueta y asigna a un agente si el lead califica.',
		icon: 'kanban',
		trigger_type: 'keyword',
		trigger_config: { keywords: ['precio', 'cotización', 'cotizacion', 'info'], match: 'any' },
		start_node_id: 'ask',
		nodes: {
			ask:  { id: 'ask',  type: 'ask',       config: { question: '¡Con gusto te cotizamos! 💬 ¿Qué presupuesto aproximado manejas? (escribe "alto" si supera los ₡100.000)', var_name: 'presupuesto' }, next: 'cond' },
			cond: {
				id: 'cond', type: 'condition',
				config: { var_name: 'presupuesto', op: 'contains', value: 'alto' },
				branches: { yes: 'tagHot', no: 'msgLow' }, next: null
			},
			// Nota: agrega un nodo "Asignar" después de esto y elige tu agente/equipo,
			// o deja que el handoff escale al equipo disponible.
			tagHot: { id: 'tagHot', type: 'label',   config: { labels: ['Lead caliente'], op: 'add' }, next: 'hLead' },
			hLead:  { id: 'hLead',  type: 'handoff', config: { reason: 'Lead calificado (presupuesto alto)' }, next: null },
			msgLow: { id: 'msgLow', type: 'message', config: { text: '¡Gracias! 🙏 Te enviaremos opciones que se ajusten a tu presupuesto. Un agente te contactará pronto.' }, next: 'fin' },
			fin:    { id: 'fin',    type: 'end',     config: {}, next: null }
		}
	},

	// ─────────────────────────────────────────────────────────────────────
	{
		id: 'after_hours',
		name: 'Fuera de horario',
		description: 'Responde distinto según si es horario laboral, con opción de dejar un mensaje.',
		icon: 'circle',
		trigger_type: 'message_in',
		trigger_config: {},
		start_node_id: 'cond',
		nodes: {
			// `horario_laboral` debería setearlo otro nodo o el contexto; por defecto
			// la rama "no" cubre el caso fuera de horario. El usuario puede ajustar la
			// condición en el editor (op/value) según cómo marque su horario.
			cond: {
				id: 'cond', type: 'condition',
				config: { var_name: 'horario_laboral', op: 'equals', value: 'si' },
				branches: { yes: 'abierto', no: 'cerrado' }, next: null
			},
			abierto: { id: 'abierto', type: 'message', config: { text: '¡Hola! 👋 Estamos en línea. ¿En qué te ayudamos?' }, next: 'fin' },
			cerrado: {
				id: 'cerrado', type: 'menu',
				config: {
					question: 'Gracias por escribir 🌙 Ahora estamos fuera de horario. ¿Qué prefieres?',
					var_name: 'fuera_opcion',
					options: [{ label: 'Dejar un mensaje' }, { label: 'Es urgente' }],
					max_attempts: 2
				},
				branches: { '0': 'mensaje', '1': 'urgente', 'other': 'mensaje' }, next: null
			},
			mensaje: { id: 'mensaje', type: 'message', config: { text: 'Perfecto ✍️ Dejanos tu mensaje y te responderemos apenas abramos. ¡Gracias!' }, next: 'fin' },
			urgente: { id: 'urgente', type: 'handoff', config: { reason: 'Caso urgente fuera de horario' }, next: null },
			fin:     { id: 'fin',     type: 'end',     config: {}, next: null }
		}
	}
];

/**
 * Clona una plantilla y reescribe todos los IDs de nodo a IDs únicos, para que
 * cada flujo creado sea independiente. Devuelve el objeto listo para create().
 */
export function instantiate(tpl) {
	const idMap = {};
	let counter = 0;
	const uniq = () => 'n_' + (Date.now().toString(36)) + (counter++).toString(36);

	// Mapear cada id lógico -> id único
	for (const oldId of Object.keys(tpl.nodes)) {
		idMap[oldId] = uniq();
	}

	const remap = (oldId) => (oldId == null ? null : (idMap[oldId] ?? oldId));

	const nodes = {};
	for (const [oldId, node] of Object.entries(tpl.nodes)) {
		const newId = idMap[oldId];
		const copy = {
			id: newId,
			type: node.type,
			config: JSON.parse(JSON.stringify(node.config || {})),
			next: remap(node.next ?? null)
		};
		if (node.branches) {
			copy.branches = {};
			for (const [k, v] of Object.entries(node.branches)) {
				copy.branches[k] = remap(v);
			}
		}
		if (node.pos) copy.pos = { ...node.pos };
		nodes[newId] = copy;
	}

	return {
		name: tpl.name,
		description: tpl.description || '',
		is_active: false,
		trigger_type: tpl.trigger_type || 'manual',
		trigger_config: JSON.parse(JSON.stringify(tpl.trigger_config || {})),
		start_node_id: remap(tpl.start_node_id),
		nodes
	};
}

/**
 * Descripciones de cada tipo de nodo — para el glosario del editor.
 * Las claves coinciden con NODE_META en FlowEditor.svelte.
 */
export const NODE_HELP = {
	message:   'Envía un mensaje al cliente. Soporta variables como {{nombre}} o {{contact.name}}.',
	ask:       'Hace una pregunta y espera la respuesta del cliente, guardándola en una variable.',
	menu:      'Muestra opciones numeradas. El cliente responde con el número (o el texto) y el flujo se ramifica por opción.',
	condition: 'Decide entre dos caminos (sí / no) según el valor de una variable.',
	condition_is_new_contact: 'Ramifica según si el contacto es nuevo o ya existía. Conecta ambas salidas.',
	condition_has_label:      'Ramifica según si el contacto/conversación tiene cierta etiqueta.',
	ai_agent:  'Deja que un Agente IA responda un turno de la conversación.',
	handoff:   'Escala la conversación a un humano. Termina el flujo automático.',
	tag:       'Agrega o quita etiquetas al contacto.',
	assign:    'Asigna la conversación a un agente o equipo.',
	label:     'Agrega o quita etiquetas a la conversación.',
	status:    'Cambia el estado de la conversación (abierta, pendiente, resuelta...).',
	webhook:   'Envía los datos de la conversación a una URL externa (integración).',
	wait:      'Pausa el flujo unos segundos antes de continuar.',
	end:       'Marca el final del flujo.'
};
